import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Calendar } from "primereact/calendar";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { StatusBadge } from "../ui/StatusBadge";
import { Invoice, InvoiceStatusEnum } from "../../types/invoice.types";
import { getAllInvoices } from "../../service/invoice/InvoiceServices";
import { PaginationInputDto } from "../../model/common/PaginatedInputDto";
import { Toast } from "primereact/toast";
import moment from "moment";
import { PermissionVariables } from "../../shared/constant/PermissionVariables";
import { faEye, faPencil } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MultiSelect } from "primereact/multiselect";
import { getAllClients } from "../../service/client/ClientServices";
import { ClientList } from "../../types/client.types";
import { SelectButton } from "primereact/selectbutton";
import { GetPaginatedInvoiceDto } from "@/dtos/invoice/GetPaginatedInvoiceDto";

export const InvoiceList: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedInvoices, setSelectedInvoices] = useState<Invoice[]>([]);
  const [filters, setFilters] = useState({
    global: { value: "", matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    clientName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    industry: { value: null, matchMode: FilterMatchMode.EQUALS },
    issueDate: { value: null, matchMode: FilterMatchMode.DATE_IS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 5,
    page: 1,
    sortField: undefined,
    sortOrder: undefined,
  });
  const [selectedClients, setSelectedClients] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const dispatch = useDispatch();
  const userPermissions = useSelector(
    (state: any) => state.permission.permissions
  );
  const navigate = useNavigate();
  const toast = React.useRef<Toast>(null);
  const localization = useSelector((state: any) => state.localization);
  const statusOptions = [
    { name: "Pending", value: "pending" },
    { name: "Paid", value: "paid" },
    { name: "Write Off", value: "write_off" },
    { name: "Draft", value: "draft" },
    { name: "Partial", value: "partial" },
  ];
  const [statusValue, setStatusValue] = useState<string | null>("pending");

  const [dates, setDates] = useState<Date[] | null>([
  new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
]);

  const fetchClients = async () => {
    const response = await getAllClients(dispatch, {
      Page: 1,
      Limit: 100,
      SortProperty: "client_name",
      SortBy: "ASC",
    });

    if (response) {
      const options = [
        ...response?.List?.map((client: ClientList) => ({
          name: client.client_name,
          code: client.client_id.toString(),
          id: client.client_id,
        })),
      ];
      setClients(options);
    }
  };
  const fetchInvoices = async () => {
    try {

      const params: GetPaginatedInvoiceDto = {
        Page: lazyParams.page,
        Limit: lazyParams.rows,
        SortProperty: lazyParams.sortField,
        SortBy: lazyParams.sortOrder === 1 ? "ASC" : "DESC",
        Filter: globalFilterValue ? globalFilterValue : undefined,
        PropertyNames: globalFilterValue
          ? ["invoice_number", "client_name"]
          : undefined,
        dates:
          dates && dates?.length > 0
            ? dates?.map((date) => moment(date).format("YYYY-MM-DD"))
            : [],
        status: statusValue ?? undefined,
        clientId:
          selectedClients.length > 0 ? selectedClients.map((c) => c.id) : [],
      };
      const response = await getAllInvoices(dispatch, params);

      if (response) {
        setInvoices(response.List);
        setTotalRecords(response.TotalCount);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.current?.show({
        severity: "error",
        summary: localization?.Error || "Error",
        detail: localization?.FailedToLoadInvoices || "Failed to load invoices",
      });
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [
    lazyParams,
    globalFilterValue,
    filters,
    dates,
    selectedClients,
    statusValue,
  ]);
  useEffect(() => {
    fetchClients();
  }, []);

  const onPage = (event: any) => {
    setLazyParams((prev) => ({
      ...prev,
      first: event.first,
      rows: event.rows,
      page: event.page + 1,
    }));
  };

  const onSort = (event: any) => {
    setLazyParams((prev) => ({
      ...prev,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    }));
  };

  // Column Templates
  const invoiceNumberTemplate = (rowData: any) => {
    return (
      <a
        href={`/invoice/${rowData.id}`}
        className="text-blue-600 hover:underline font-medium"
        onClick={(e) => {
          e.preventDefault();
          navigate(`/invoice/${rowData?.invoice_id}`);
        }}
      >
        {rowData?.invoice_number}
      </a>
    );
  };

  const statusTemplate = (rowData: Invoice) => {
    return <StatusBadge status={rowData.status} />;
  };

  const amountTemplate = (rowData: Invoice) => {
    return (
      <span className="font-medium">
        {rowData.currency_symbol}
        {rowData.total}
      </span>
    );
  };

  const dateTemplate = (rowData: Invoice) => {
    return moment(rowData.issue_date).format("DD/MM/YY");
  };
  const paidDateTemplate = (rowData: Invoice) => {
    return rowData.last_payment_date?moment(rowData.last_payment_date).format("DD/MM/YY"):"-";
  };

  const dueDateTemplate = (rowData: Invoice) => {
    return moment(rowData?.due_date).format("DD/MM/YY");
  };

  const industryTemplate = (rowData: any) => {
    return rowData?.industry_name || "-";
  };

  const header = (
    <div className="flex md:flex-wrap items-center justify-between gap-4 p-2 bg-white rounded-lg shadow-sm">
      <SelectButton
            value={statusValue}
            onChange={(e) => setStatusValue(e.value)}
            optionLabel="name"
            options={statusOptions}
            className="border border-gray-200 rounded"
          />
      <div className="flex flex-wrap items-end gap-4">
        <MultiSelect
            value={selectedClients}
            onChange={(e) => setSelectedClients(e.value)}
            options={clients}
            optionLabel="name"
            placeholder="Select Clients"
            maxSelectedLabels={3}
            className="w-full md:w-64"
          />
        <div className="w-full md:w-auto">
          <Calendar
            id="dates"
            value={dates}
            onChange={(e: any) => setDates(e.value)}
            showIcon
            selectionMode="range"
            readOnlyInput
            hideOnRangeSelection
            showButtonBar
            className="w-full"
          />
        </div>
        {userPermissions?.includes(PermissionVariables.createInvoice) && (
        <Button
          onClick={() => navigate("/invoice/create")}
          className="bg-gold shadow-sm py-2 text-sm w-[135px]"
        >
          {localization?.CreateInvoice || "Create Invoice"}
        </Button>
      )}
      </div>
    </div>
  );

  const actionTemplate = (rowData: Invoice) => {
    return (
      <div className="flex gap-1">
        <Button
          className=" py-1 px-2 text-xs"
          onClick={() => navigate(`/invoice/${rowData.invoice_id}`)}
        >
          <FontAwesomeIcon icon={faEye} /> {localization?.View || "View"}
        </Button>
        {rowData.status == InvoiceStatusEnum.DRAFT && (
          <Button
            className=" text-white hover:bg-gold py-1 px-2 text-xs"
            onClick={() => navigate(`/invoice/edit/${rowData.invoice_id}`)}
          >
            <FontAwesomeIcon icon={faPencil} /> {localization?.Edit || "Edit"}
          </Button>
        )}
      </div>
    );
  };

  const clientTemplate = (rowData: any) => {
    return <div>{rowData?.client_name}</div>;
  };

  return (
    <div className="rounded-md">
      <Toast ref={toast} />

      <h1 className="text-xl font-bold surface-900 mb-4">
        {localization?.Invoices || "Invoices"}
      </h1>

      <Card className="shadow-sm border-t-0 border-r-0 border-b-0 border-l-4 p-0 card_content_padding">
        <DataTable
          value={invoices}
          lazy
          paginator
          first={lazyParams.first}
          rows={lazyParams.rows}
          totalRecords={totalRecords}
          onPage={onPage}
          onSort={onSort}
          sortField={lazyParams.sortField}
          sortOrder={lazyParams.sortOrder}
          filters={filters}
          globalFilterFields={[
            "invoiceNumber",
            "clientName",
            "status",
            "industry",
          ]}
          header={header}
          emptyMessage={localization?.NoInvoicesFound || "No invoices found"}
          responsiveLayout="scroll"
          selection={selectedInvoices}
          onSelectionChange={(e) => setSelectedInvoices(e.value)}
          selectionMode="multiple"
          dataKey="id"
          stripedRows
          className="p-datatable-sm"
          rowHover
          size="small"
          rowsPerPageOptions={[5, 10, 20, 50]}
        >
          <Column
            field="invoice_number"
            header={localization?.Invoice || "Invoice"}
            body={invoiceNumberTemplate}
            sortable
            headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
            bodyClassName="py-2 text-sm"
          />
          <Column
            field="client"
            header={localization?.Client || "Client"}
            sortable
            headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
            bodyClassName="py-2 text-sm"
            body={clientTemplate}
          />
          <Column
            field="issueDate"
            header={localization?.IssueDate || "Issue Date"}
            body={dateTemplate}
            sortable
            headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
            bodyClassName="py-2 text-sm"
          />
          {statusValue == InvoiceStatusEnum.PAID ? (
            <Column
              field="last_payment_date"
              header={localization?.PaidDate || "Paid Date"}
              body={paidDateTemplate}
              sortable
              headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
              bodyClassName="py-2 text-sm"
            />
          ) : (
            <Column
              field="dueDate"
              header={localization?.DueDate || "Due Date"}
              body={dueDateTemplate}
              sortable
              headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
              bodyClassName="py-2 text-sm"
            />
          )}
          <Column
            field="industry_name"
            header={localization?.Industry || "Industry"}
            body={industryTemplate}
            headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
            bodyClassName="py-2 text-sm"
          />
          <Column
            field="status"
            header={localization?.Status || "Status"}
            body={statusTemplate}
            headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
            bodyClassName="py-2 text-sm"
          />
          <Column
            field="total"
            header={localization?.Amount || "Amount"}
            body={amountTemplate}
            sortable
            style={{ textAlign: "left" }}
            headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
            bodyClassName="py-2 text-sm"
          />
          <Column
            body={actionTemplate}
            header={localization?.Actions || "Actions"}
            style={{ textAlign: "center", width: "10rem" }}
            headerClassName="bg-gray-100 text-gray-700 text-xs py-2"
            bodyClassName="py-2"
          />
        </DataTable>
      </Card>
    </div>
  );
};
